/**
 * 이상한 방 - 매치 클라이언트의 시계 보간 (step.atMs + 2Hz clock → 지금 화면에 찍을 시간)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 11.3절(시간은 서버가 쥔다)
 *            로드맵 모델 2.4절(`clock` 2Hz - 틱마다 스냅샷을 보내지 않는 이유) · 2.5절(클라이언트는 `clock`으로 시계 보간)
 *            M2 첫 몫 기록: "시계만 흐른 틱은 `step`을 내지 않는다 → 시간 값은 `step.atMs` + `clock`으로 클라이언트가 잇는다"
 *
 * ## 왜 필요한가
 *
 * 호스트는 상태가 바뀔 때만 `step`을 보내고(`net/matchHost.commit`), 시계만 흐른 틱에는 초당 두 번 `clock{phaseElapsedMs}`만
 * 보낸다. 뷰의 시간 값(`phaseRemainingMs` · 원래 방 게이지의 `remainingMs`)은 **마지막 `step`이 만들어진 시각의 값**이라
 * 그대로 그리면 HUD 시계가 2Hz로 덜컥거리거나, 조용한 구간(사격이 없는 30초)에는 통째로 멈춘다.
 * 그래서 클라이언트가 "마지막 step 이후 호스트 시계가 얼마나 흘렀는가"(`sinceStepMs`)를 추정하고, 뷰의 시간 값에서
 * 그만큼을 뺀다. 규칙이 아니라 **표시 보간**이다 - 마감·쿨타임 판정은 여전히 호스트가 하고, 여기 값은 화면에만 간다.
 *
 * ## 추정의 재료 둘
 *
 *  1. **프레임 델타.** `step.atMs`를 기준으로 프레임마다 `dt`를 더한다. 루프백에서는 프레임 델타가 곧 호스트 시계라
 *     (`loopbackTransport.ts` 머리말) 이 추정이 정확하다.
 *  2. **`clock` 보정.** 소켓 너머(M6)에서는 프레임 델타와 서버 시계가 서서히 갈린다(탭이 잠들면 크게). 기준점(`anchor`)
 *     이후 호스트가 흐른 양(`phaseElapsedMs`의 차)과 내가 흐른 양의 차이만큼 추정을 당긴다. **기준점은 `step`이 앉힌다** -
 *     `step`이 `atMs`와 `phaseElapsedMs`를 함께 싣기 때문에 그 직후의 첫 `clock`부터 대조가 된다. 기준을 첫 `clock`에서
 *     잡으면 `step`과 그 `clock` 사이의 드리프트는 영영 못 잡는다. `phaseElapsedMs`가 줄어들면 페이즈가 바뀐 것이라
 *     기준만 다시 잡는다(그 사이 `step`이 이미 `atMs`를 다시 앉혔다).
 *
 * ## 자막 잠금 동안의 드리프트
 *
 * 페이즈 자막이 떠 있는 동안 세션은 이 시계도 `transport.tick`도 함께 멈춘다(`matchSession.update`). 루프백에서는 호스트도
 * 그동안 멈추므로 둘이 같이 서고, 소켓에서는 서버가 계속 흐르므로 자막이 걷힌 뒤 첫 `clock`이 그 차이를 한 번에 당긴다 -
 * `step`이 기준을 앉혀 두었기 때문에 가능한 보정이다. 어느 쪽에서든 추정만 앞서가는 경우(잠금 중 이 시계만 흐른다)는 없다.
 *
 * `Date.now()`·`performance.now()`를 읽지 않는다 - 들어오는 것은 호스트가 찍은 ms와 프레임 델타뿐이다. 그래서 순수하게
 * 테스트된다(`matchClock.test.ts`). 이 파일은 `core`·`game` 리프만 본다.
 */

import type { DoorView } from './match/matchView'
import type { OriginRoomStatus } from './originVisit'

export type MatchClock = Readonly<{
  /** `step`이 왔다. 그 안의 시간 값은 이 시각의 것이다 - 추정을 여기에 다시 앉히고, 페이즈 경과로 `clock`의 기준점을 잡는다. */
  onStep: (atMs: number, phaseElapsedMs: number) => void
  /** 2Hz `clock`. 기준점(`step` 또는 앞 `clock`)과의 차이로 추정의 드리프트를 보정한다. */
  onClock: (phaseElapsedMs: number) => void
  /** 한 프레임(ms). 유한한 양수만 흐른다 - 탭이 잠든 뒤의 거대한 dt도 그대로 흐른다(루프백에서는 그것이 호스트 시계다). */
  advance: (dtMs: number) => void
  /** 마지막 `step` 이후 흐른 것으로 추정되는 호스트 ms. `step`이 온 적 없으면 0. */
  sinceStepMs: () => number
  /** 지금 호스트 시각의 추정(ms). 수색 소요 시간 같은 "호스트 시각끼리의 차"에 쓴다. */
  hostNowMs: () => number
}>

/** 보정의 기준점(`step` 또는 앞 `clock`). `elapsed`는 호스트가 말한 페이즈 경과, `hostNow`는 그때의 내 추정. */
type ClockAnchor = Readonly<{ elapsed: number; hostNow: number }>

export function createMatchClock(): MatchClock {
  let hostNow = 0
  let stepAt = 0
  let anchor: ClockAnchor | null = null

  return {
    onStep(atMs: number, phaseElapsedMs: number): void {
      if (!Number.isFinite(atMs) || !Number.isFinite(phaseElapsedMs)) return
      hostNow = atMs
      stepAt = atMs
      // `step`이 시각과 경과를 함께 앉혔다 - 다음 `clock`은 이 점과 대조한다. 앞 `clock`과 비교하면 이미 반영된 보정을 두 번 넣는다.
      anchor = { elapsed: phaseElapsedMs, hostNow: atMs }
    },

    onClock(phaseElapsedMs: number): void {
      if (!Number.isFinite(phaseElapsedMs)) return
      const previous = anchor
      anchor = { elapsed: phaseElapsedMs, hostNow }
      // `step` 없이 온 첫 `clock`이거나 페이즈가 바뀌었다(경과가 줄었다). 차이를 낼 기준이 없으니 기준만 잡는다.
      if (previous === null || phaseElapsedMs < previous.elapsed) return
      const hostAdvanced = phaseElapsedMs - previous.elapsed
      const localAdvanced = hostNow - previous.hostNow
      hostNow += hostAdvanced - localAdvanced
      anchor = { elapsed: phaseElapsedMs, hostNow }
    },

    advance(dtMs: number): void {
      if (!(dtMs > 0) || !Number.isFinite(dtMs)) return
      hostNow += dtMs
    },

    sinceStepMs: () => Math.max(0, hostNow - stepAt),
    hostNowMs: () => hostNow,
  }
}

/* --- 뷰의 시간 값에 적용 ------------------------------------------------------------- */

/** 남은 시간에서 흐른 만큼을 뺀다. 0 아래로는 내려가지 않는다(마감을 넘긴 표시는 호스트의 `step`이 바로잡는다). */
export function remainingAfter(remainingMs: number | null, sinceMs: number): number | null {
  if (remainingMs === null) return null
  return Math.max(0, remainingMs - Math.max(0, sinceMs))
}

/**
 * 원래 방 버튼의 얼굴에서 흐르는 두 값(체류 · 쿨타임)만 당긴다. `totalMs`(게이지 분모)와 종류는 그대로다 -
 * 종류를 바꾸는 것(체류 → 쿨타임)은 규칙이고 호스트의 `step`이 한다. 여기서 `remainingMs`가 0에 닿아도 종류는 남는다.
 */
export function originStatusAfter(status: OriginRoomStatus, sinceMs: number): OriginRoomStatus {
  switch (status.kind) {
    case 'staying':
    case 'cooldown':
      return { ...status, remainingMs: Math.max(0, status.remainingMs - Math.max(0, sinceMs)) }
    case 'ready':
    case 'unavailable':
      return status
  }
}

/**
 * 문 앞의 두 시계(보유자의 남은 체류 · 예약 유예 잔여)만 당긴다(M5 규칙 5). 보유자·줄·예약자는 그대로다 - 누가 나갔는지
 * (`holder`가 `null`이 되는 것)는 규칙이고 호스트의 `step`이 한다. `remainingAfter`와 같은 클램프라 0 아래로 내려가지 않는다.
 * HUD(`ui/doorQueue.ts`)는 이 결과를 받아 그리기만 하고 "몇 초 남았나"를 스스로 세지 않는다.
 */
export function doorAfter(door: DoorView, sinceMs: number): DoorView {
  const since = Math.max(0, sinceMs)
  return {
    ...door,
    holderRemainingMs: Math.max(0, door.holderRemainingMs - since),
    reservedRemainingMs: Math.max(0, door.reservedRemainingMs - since),
  }
}
